import { useEffect, useState, useRef } from "react";
import { getDepartmentList } from "../redux/slices/employee";
import { dispatch, useSelector } from "../redux/store";
import {
  ChevronDown, ChevronUp, MoreVertical, Eye, Trash2, Users,
  Calendar, DollarSign, FileText, X, Search, Check, Clock, Server,
  TrendingUp, Shield, Zap, BarChart3, Edit2, Save
} from "lucide-react";
import { salaryApi, SalaryRecord, DailyBreakdown } from "../api/salary";
import api from "../api/api";

type Employee = {
  id: string;
  first_name: string;
  middle_name?: string;
  last_name: string;
  employeeID?: string;
  email?: string;
};

const PayrollRecord = () => {
  const departmentList = useSelector((state: any) => state.employee.departmentList);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
  const [selectedEmployeesDetails, setSelectedEmployeesDetails] = useState<Employee[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedYear, setSelectedYear] = useState("2026");
  const [selectedMonth, setSelectedMonth] = useState("03");
  const [isLoading, setIsLoading] = useState(false);
  const [employeesLoading, setEmployeesLoading] = useState(true);
  const [salaryRecords, setSalaryRecords] = useState<SalaryRecord[]>([]);
  const [dailyData, setDailyData] = useState<DailyBreakdown[]>([]);
  const [selectedRecord, setSelectedRecord] = useState<SalaryRecord | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [showAdjustDialog, setShowAdjustDialog] = useState(false);
  const [message, setMessage] = useState("");
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<'attendance' | 'system'>('attendance');
  const [adjustmentData, setAdjustmentData] = useState({
    bonus: 0,
    extrapay: 0,
    description: ""
  });
  const [isAdjusting, setIsAdjusting] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const months = [
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  const years = [2024, 2025, 2026, 2027].map((year) => ({
    value: year.toString(),
    label: year.toString(),
  }));

  useEffect(() => {
    dispatch(getDepartmentList());
  }, []);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuId(null);
      }
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowEmployeeDropdown(false);
        setEmployeeSearchQuery("");
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  // Fetch employees
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setEmployeesLoading(true);
        const response = await api.get("/employeesAll");
        setEmployees(response.data.employees || []);
      } catch (error) {
        console.error("Failed to fetch employees:", error);
        setMessage("Failed to load employees");
      } finally {
        setEmployeesLoading(false);
      }
    };
    fetchEmployees();
  }, []);

  // Update selected employees details when selection changes
  useEffect(() => {
    const selectedDetails = employees.filter(emp => selectedEmployeeIds.includes(emp.id));
    setSelectedEmployeesDetails(selectedDetails);
  }, [selectedEmployeeIds, employees]);

  // Filter employees based on search query
  const filteredEmployees = employees.filter(emp => {
    const fullName = `${emp.first_name} ${emp.middle_name || ''} ${emp.last_name}`.toLowerCase();
    const searchTerm = employeeSearchQuery.toLowerCase();
    return fullName.includes(searchTerm) || (emp.employeeID && emp.employeeID.toLowerCase().includes(searchTerm));
  });

  // Helper functions for multiple selection
  const toggleEmployeeSelection = (employeeId: string) => {
    setSelectedEmployeeIds((prev) => {
      if (prev.includes(employeeId)) {
        return prev.filter((id) => id !== employeeId);
      } else {
        return [...prev, employeeId];
      }
    });
    setSelectAll(false);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedEmployeeIds([]);
    } else {
      setSelectedEmployeeIds(employees.map((emp) => emp.id));
    }
    setSelectAll(!selectAll);
  };

  const removeEmployee = (employeeId: string) => {
    setSelectedEmployeeIds((prev) => prev.filter((id) => id !== employeeId));
    setSelectAll(false);
  };

  const isEmployeeSelected = (employeeId: string) => {
    return selectedEmployeeIds.includes(employeeId);
  };

  // Format date helper
  const formatDateForDisplay = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-IN");
    } catch {
      return dateString;
    }
  };

  // Format Department name
  const formatDepartment = (dept: string): string => {
    if (!dept) return "General";
    return dept
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Format Salary ID with padding
  const formatSalaryId = (salaryId: number | string): string => {
    return `${String(salaryId).padStart(3, "0")}`;
  };

  // Calculate salary based on active tab
  const handleCalculate = async () => {
    if (!selectedEmployeeIds.length || !selectedYear || !selectedMonth) {
      setMessage("Please select at least one Employee, Year, and Month");
      return;
    }

    setIsLoading(true);
    setMessage("");
    setSalaryRecords([]);

    try {
      let response;

      // Use different API based on active tab
      if (activeTab === 'attendance') {
        response = await salaryApi.calculateBulk(selectedYear, selectedMonth, selectedEmployeeIds);
      } else {
        response = await salaryApi.calculateWithSystemTracking(selectedYear, selectedMonth, selectedEmployeeIds);
      }

      const records: SalaryRecord[] = response.data.results.map(
        (record: any) => {
          const salaryDate = record.SalaryDate || new Date().toLocaleDateString("en-IN");
          const mode = record.mode || "Bank Transfer";
          const department = formatDepartment(record.department || "General");

          return {
            ...record,
            SalaryDate: salaryDate,
            mode: mode,
            department: department,
          };
        }
      );

      setSalaryRecords(records);
      setMessage(
        `✅ Salary calculated successfully for ${selectedEmployeeIds.length} employee(s) using ${activeTab === 'attendance' ? 'Attendance Based' : 'System Level'} tracking!`
      );
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.detail || "Failed to calculate salary";
      setMessage(`❌ ${errorMsg}`);
      setSalaryRecords([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNoDeduction = async () => {
    if (!selectedEmployeeIds.length || !selectedYear || !selectedMonth) {
      setMessage("Please select Employee, Year, Month");
      return;
    }

    setIsLoading(true);
    setMessage("");
    setSalaryRecords([]);

    try {
      let response;

      if (activeTab === "attendance") {
        response = await salaryApi.calculateNoDeduction(
          selectedYear,
          selectedMonth,
          selectedEmployeeIds
        );
      } else {
        response = await salaryApi.calculateNoDeductionSystem(
          selectedYear,
          selectedMonth,
          selectedEmployeeIds
        );
      }

      const records = response.data.results.map((record: any) => ({
        ...record,
        SalaryDate: record.SalaryDate || new Date().toLocaleDateString("en-IN"),
        mode: record.mode || "Bank Transfer",
        department: formatDepartment(record.department || "General"),
      }));

      setSalaryRecords(records);
      setMessage("✅ Salary calculated WITHOUT deductions!");
    } catch (error: any) {
      setMessage(error?.response?.data?.detail || "❌ Failed");
    } finally {
      setIsLoading(false);
    }
  };

  // Manual Salary Adjustment Handler
  const handleAdjustSalary = (record: SalaryRecord) => {
    console.log("Adjust salary clicked for:", record.name);
    console.log("Current bonus:", record.bonus);
    console.log("Current extrapay:", record.extrapay);

    setSelectedRecord(record);
    setAdjustmentData({
      bonus: record.bonus || 0,
      extrapay: record.extrapay || 0,
      description: ""
    });
    setShowAdjustDialog(true);
    setOpenMenuId(null);
  };

  // Submit Salary Adjustment
  const handleSubmitAdjustment = async () => {
    if (!selectedRecord) return;

    setIsAdjusting(true);
    try {
      // Call the manual salary adjustment API
      await salaryApi.manualSalaryAdjustment({
        employee_id: selectedRecord.employee_id,
        bonus: adjustmentData.bonus,
        extrapay: adjustmentData.extrapay,
        month: parseInt(selectedMonth),
        year: parseInt(selectedYear),
        description: adjustmentData.description || "Manual adjustment"
      });

      setMessage(`✅ Salary adjustment applied successfully for ${selectedRecord.name}!`);

      // Close the adjustment dialog
      setShowAdjustDialog(false);

      // Recalculate salary to get updated data with bonus and extra pay
      await recalculateSalary();

      setAdjustmentData({ bonus: 0, extrapay: 0, description: "" });
      setSelectedRecord(null);
    } catch (error: any) {
      console.error("Failed to adjust salary:", error);
      setMessage(error?.response?.data?.detail || "❌ Failed to adjust salary");
    } finally {
      setIsAdjusting(false);
      setTimeout(() => setMessage(""), 5000);
    }
  };

  // Recalculate salary after adjustment
  const recalculateSalary = async () => {
    if (!selectedEmployeeIds.length || !selectedYear || !selectedMonth) return;

    setIsLoading(true);
    try {
      let response;

      if (activeTab === 'attendance') {
        response = await salaryApi.calculateBulk(selectedYear, selectedMonth, selectedEmployeeIds);
      } else {
        response = await salaryApi.calculateWithSystemTracking(selectedYear, selectedMonth, selectedEmployeeIds);
      }

      const records: SalaryRecord[] = response.data.results.map(
        (record: any) => {
          const salaryDate = record.SalaryDate || new Date().toLocaleDateString("en-IN");
          const mode = record.mode || "Bank Transfer";
          const department = formatDepartment(record.department || "General");

          return {
            ...record,
            SalaryDate: salaryDate,
            mode: mode,
            department: department,
          };
        }
      );

      setSalaryRecords(records);
      setMessage(`✅ Salary recalculated successfully with adjustments!`);
    } catch (error: any) {
      console.error("Failed to recalculate salary:", error);
      setMessage("❌ Failed to recalculate salary");
    } finally {
      setIsLoading(false);
      setTimeout(() => setMessage(""), 3000);
    }
  };

  // Fetch daily breakdown data
  const handleViewMore = async (record: SalaryRecord) => {
    try {
      setDailyData(record?.logs?.attendance || []);
      setSelectedRecord(record);
      setShowDialog(true);
      setOpenMenuId(null);
    } catch (error) {
      console.error("Failed to fetch daily breakdown:", error);
    }
  };

  // Delete handler
  const handleDelete = (record: SalaryRecord) => {
    if (
      window.confirm(
        `Delete salary record ${formatSalaryId(record.salary_id)} for ${record.name}?`
      )
    ) {
      setSalaryRecords((prev) =>
        prev.filter((r) => r.salary_id !== record.salary_id)
      );
      setMessage("🗑️ Salary record deleted successfully!");
      setOpenMenuId(null);
    }
  };

  // Get selected employees count text
  const getSelectedCountText = () => {
    if (selectedEmployeeIds.length === 0) return "Select employees";
    if (selectedEmployeeIds.length === employees.length) return `All (${employees.length})`;
    return `${selectedEmployeeIds.length} selected`;
  };

  // Enhanced Tab Component with longer tabs and equal centering
  const PayrollTabs = () => (
    <div className="border-b border-gray-200 bg-white">
      <div className="flex justify-center">
        <div className="flex" role="tablist">
          {/* Attendance Based Tab - Longer with equal padding */}
          <button
            onClick={() => setActiveTab('attendance')}
            className={`
              relative px-12 py-4 text-base font-medium transition-all duration-200
              min-w-[200px] md:min-w-[280px]
              ${activeTab === 'attendance'
                ? 'text-indigo-600'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }
            `}
            role="tab"
            aria-selected={activeTab === 'attendance'}
          >
            <div className="flex items-center justify-center gap-3">
              <Clock className={`w-5 h-5 ${activeTab === 'attendance' ? 'text-indigo-600' : 'text-gray-400'}`} />
              <div className="text-left">
                <div className="font-semibold">Attendance Based</div>
                <div className="text-xs text-gray-400 hidden md:block">Calculate from daily attendance</div>
              </div>
            </div>
            {activeTab === 'attendance' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 rounded-full"></div>
            )}
          </button>

          {/* System Level Tab - Longer with equal padding */}
          <button
            onClick={() => setActiveTab('system')}
            className={`
              relative px-12 py-4 text-base font-medium transition-all duration-200
              min-w-[200px] md:min-w-[280px]
              ${activeTab === 'system'
                ? 'text-emerald-600'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }
            `}
            role="tab"
            aria-selected={activeTab === 'system'}
          >
            <div className="flex items-center justify-center gap-3">
              <Server className={`w-5 h-5 ${activeTab === 'system' ? 'text-emerald-600' : 'text-gray-400'}`} />
              <div className="text-left">
                <div className="font-semibold">System Level</div>
                <div className="text-xs text-gray-400 hidden md:block">Track via system logs</div>
              </div>
            </div>
            {activeTab === 'system' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-600 rounded-full"></div>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  // Tab Information Panel
  const TabInfoPanel = () => (
    <div className={`
      mx-6 mt-4 p-4 rounded-lg border transition-all duration-300
      ${activeTab === 'attendance'
        ? 'bg-indigo-50 border-indigo-200'
        : 'bg-emerald-50 border-emerald-200'
      }
    `}>
      <div className="flex items-start gap-3">
        {activeTab === 'attendance' ? (
          <>
            <div className="p-2 bg-indigo-100 rounded-lg">
              <TrendingUp className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h3 className="font-semibold text-indigo-900 text-sm md:text-base">
                Attendance Based Tracking
              </h3>
              <p className="text-indigo-700 text-xs md:text-sm mt-1">
                Salary is calculated based on daily attendance records, including present days,
                absences, overtime hours, and applicable deductions. This method uses real-time
                attendance data from the system.
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-xs">
                  <Zap className="w-3 h-3" />
                  Real-time tracking
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-xs">
                  <Calendar className="w-3 h-3" />
                  Daily attendance
                </span>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="p-2 bg-emerald-100 rounded-lg">
              <Shield className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-semibold text-emerald-900 text-sm md:text-base">
                System Level Tracking
              </h3>
              <p className="text-emerald-700 text-xs md:text-sm mt-1">
                Salary is calculated using comprehensive system logs and automated tracking mechanisms.
                This method provides enhanced accuracy by cross-referencing multiple data sources
                including system entries, audit trails, and automated validations.
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs">
                  <BarChart3 className="w-3 h-3" />
                  Advanced analytics
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs">
                  <Shield className="w-3 h-3" />
                  Enhanced accuracy
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );

  if (employeesLoading) {
    return (
      <div className="h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white shadow-xl rounded-2xl p-12 text-center max-w-md w-full">
          <div className="flex flex-col items-center space-y-6">
            <div className="w-20 h-20 bg-gradient-to-r from-emerald-400 to-blue-500 rounded-2xl shadow-xl animate-pulse flex items-center justify-center">
              <Users className="w-10 h-10 text-white" />
            </div>
            <div className="text-xl font-bold text-gray-700">
              Loading Employees...
            </div>
            <div className="w-48 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-emerald-400 to-blue-500 rounded-full animate-pulse w-3/4"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6">
        <div className="bg-white shadow-xl rounded-2xl overflow-hidden">
          {/* Header with gradient - Enhanced */}
          <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-6 py-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">Payroll Management</h1>
                <p className="text-indigo-100 text-sm">Calculate and manage employee salary details with precision</p>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2">
                <DollarSign className="w-4 h-4 text-white" />
                <span className="text-white text-sm font-medium">Secure Payroll Processing</span>
              </div>
            </div>
          </div>

          {/* Enhanced Tabs Component - Longer with equal centering */}
          <PayrollTabs />

          {/* Tab Information Panel */}
          <TabInfoPanel />

          {/* Selection Form */}
          <div className="p-6 bg-gradient-to-br from-gray-50 to-white border-b border-gray-200">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-3">
                <div className={`w-1 h-6 rounded-full ${activeTab === 'attendance'
                    ? 'bg-gradient-to-b from-indigo-500 to-purple-600'
                    : 'bg-gradient-to-b from-emerald-500 to-teal-600'
                  }`}></div>
                {activeTab === 'attendance' ? 'Attendance Based Calculation' : 'System Level Tracking Calculation'}
              </h2>
              <div className={`text-xs px-3 py-1 rounded-full ${activeTab === 'attendance'
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'bg-emerald-100 text-emerald-700'
                }`}>
                {activeTab === 'attendance' ? 'Real-time Data' : 'System Analytics'}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-6">
              {/* Employee Selection Dropdown - Same as before */}
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700 flex items-center gap-2">
                  <Users className="w-4 h-4 text-indigo-600" />
                  Select Employees
                </label>

                <div className="relative" ref={dropdownRef}>
                  <button
                    type="button"
                    onClick={() => setShowEmployeeDropdown(!showEmployeeDropdown)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-left flex justify-between items-center hover:border-indigo-400"
                  >
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-gray-400" />
                      <span className={`text-sm ${selectedEmployeeIds.length === 0 ? 'text-gray-500' : 'text-gray-700 font-medium'}`}>
                        {getSelectedCountText()}
                      </span>
                    </div>
                    {showEmployeeDropdown ? (
                      <ChevronUp className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    )}
                  </button>

                  {showEmployeeDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden">
                      <div className="p-2 border-b border-gray-100 bg-gray-50">
                        <div className="relative">
                          <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Search employees..."
                            value={employeeSearchQuery}
                            onChange={(e) => setEmployeeSearchQuery(e.target.value)}
                            className="w-full pl-8 pr-2 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 text-sm bg-white"
                            autoFocus
                          />
                        </div>
                      </div>

                      <div className="p-2 border-b border-gray-100 bg-gray-50">
                        <button
                          onClick={handleSelectAll}
                          className={`w-full py-1.5 rounded-md font-medium text-xs transition-all flex items-center justify-center gap-1.5 ${selectAll
                              ? "bg-emerald-500 text-white hover:bg-emerald-600"
                              : "bg-white text-indigo-600 hover:bg-indigo-50 border border-indigo-200"
                            }`}
                        >
                          {selectAll ? (
                            <>
                              <X className="w-3 h-3" />
                              Deselect All
                            </>
                          ) : (
                            <>
                              <Check className="w-3 h-3" />
                              Select All
                            </>
                          )}
                        </button>
                      </div>

                      <div className="max-h-56 overflow-y-auto">
                        {filteredEmployees.length === 0 ? (
                          <div className="p-6 text-center">
                            <p className="text-gray-500 text-xs">No employees found</p>
                          </div>
                        ) : (
                          filteredEmployees.map((emp) => {
                            const isSelected = isEmployeeSelected(emp.id);
                            return (
                              <div
                                key={emp.id}
                                className={`flex items-center p-2 cursor-pointer transition-all text-sm ${isSelected ? "bg-indigo-50" : "hover:bg-gray-50"
                                  }`}
                                onClick={() => toggleEmployeeSelection(emp.id)}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => { }}
                                  className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500 cursor-pointer"
                                />
                                <div className="ml-2 flex-1 min-w-0">
                                  <div className="font-medium text-gray-900 truncate">
                                    {emp.first_name} {emp.last_name}
                                  </div>
                                  <div className="text-xs text-gray-500 truncate">
                                    ID: {emp.id.substring(0, 8)}...
                                  </div>
                                </div>
                                {isSelected && (
                                  <Check className="w-3.5 h-3.5 text-indigo-600 flex-shrink-0" />
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>

                      <div className="p-2 border-t border-gray-100 bg-gray-50">
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-gray-600">
                            {selectedEmployeeIds.length} selected
                          </span>
                          <button
                            onClick={() => setShowEmployeeDropdown(false)}
                            className="text-indigo-600 hover:text-indigo-700 font-medium text-xs"
                          >
                            Done
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Selected Employees Chips */}
                {selectedEmployeesDetails.length > 0 && (
                  <div className="mt-2">
                    <div className="flex flex-wrap gap-1.5">
                      {selectedEmployeesDetails.slice(0, 3).map((emp) => (
                        <div
                          key={emp.id}
                          className="inline-flex items-center gap-1.5 px-2 py-1 bg-indigo-50 text-indigo-700 rounded-md text-xs font-medium"
                        >
                          <span>{emp.first_name}</span>
                          <button
                            onClick={() => removeEmployee(emp.id)}
                            className="hover:bg-indigo-100 rounded-full p-0.5 transition-colors"
                          >
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </div>
                      ))}
                      {selectedEmployeesDetails.length > 3 && (
                        <div className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-600 rounded-md text-xs font-medium">
                          +{selectedEmployeesDetails.length - 3}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Year Selection */}
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-indigo-600" />
                  Year
                </label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-sm"
                >
                  {years.map((year) => (
                    <option key={year.value} value={year.value}>
                      {year.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Month Selection */}
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-indigo-600" />
                  Month
                </label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-sm"
                >
                  {months.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Calculate Button - Enhanced */}
            {/* <div className="flex justify-center">
              <button
                onClick={handleCalculate}
                disabled={isLoading || !selectedEmployeeIds.length}
                className={`px-8 py-3 text-sm font-semibold rounded-xl shadow-lg transition-all transform flex items-center gap-3 ${
                  isLoading || !selectedEmployeeIds.length
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : activeTab === 'attendance'
                    ? "bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white hover:shadow-xl hover:-translate-y-0.5 active:scale-95"
                    : "bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 text-white hover:shadow-xl hover:-translate-y-0.5 active:scale-95"
                }`}
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Calculating...</span>
                  </>
                ) : (
                  <>
                    {activeTab === 'attendance' ? (
                      <Clock className="w-5 h-5" />
                    ) : (
                      <Server className="w-5 h-5" />
                    )}
                    <span>{activeTab === 'attendance' ? 'Calculate Salary' : 'Calculate Salary '}</span>
                  </>
                )}
              </button>
            </div> */}

            <div className="flex justify-center gap-3">
              {/* Calculate Salary */}
              <button
                onClick={handleCalculate}
                disabled={isLoading || !selectedEmployeeIds.length}
                className={`px-8 py-3 text-sm font-semibold rounded-xl shadow-lg transition-all ${isLoading || !selectedEmployeeIds.length
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-indigo-500 text-white hover:scale-95"
                  }`}
              >
                Calculate Salary
              </button>

              {/* No Deduction */}
              <button
                onClick={handleNoDeduction}
                disabled={isLoading || !selectedEmployeeIds.length}
                className={`px-8 py-3 text-sm font-semibold rounded-xl shadow-lg transition-all ${isLoading || !selectedEmployeeIds.length
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-orange-500 text-white hover:scale-95"
                  }`}
              >
                Calculate No Deduction
              </button>
            </div>
          </div>

          {/* Message */}
          {message && (
            <div
              className={`m-4 p-3 rounded-lg shadow-md border-l-4 text-sm ${message.includes("✅") || message.includes("successfully")
                  ? "bg-green-50 border-green-500 text-green-800"
                  : "bg-red-50 border-red-500 text-red-800"
                }`}
            >
              <div className="flex items-center gap-2">
                {message.includes("✅") ? (
                  <span className="text-lg">✅</span>
                ) : message.includes("❌") ? (
                  <span className="text-lg">❌</span>
                ) : null}
                <p className="font-medium">{message}</p>
              </div>
            </div>
          )}

          {/* Salary Records Table - Card View for Mobile, Table for Desktop */}
          {salaryRecords.length > 0 && (
            <div className="p-4">
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg overflow-hidden border border-green-200">
                <div className="px-4 py-3 border-b border-green-200 bg-gradient-to-r from-green-100 to-emerald-100">
                  <h2 className="text-lg font-bold text-green-800 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Salary Records ({salaryRecords.length}) - {activeTab === 'attendance' ? 'Attendance Based' : 'System Level Tracking'}
                  </h2>
                </div>

                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gradient-to-r from-green-100 to-emerald-100 border-b-2 border-green-200">
                        <th className="px-3 py-2 text-left text-xs font-bold text-green-800 uppercase tracking-wider">Employee</th>
                        <th className="px-3 py-2 text-left text-xs font-bold text-green-800 uppercase tracking-wider">Salary Date</th>
                        <th className="px-3 py-2 text-left text-xs font-bold text-green-800 uppercase tracking-wider">Mode</th>
                        <th className="px-3 py-2 text-left text-xs font-bold text-green-800 uppercase tracking-wider">Department</th>
                        <th className="px-3 py-2 text-left text-xs font-bold text-green-800 uppercase tracking-wider">Emp ID</th>
                        <th className="px-3 py-2 text-right text-xs font-bold text-green-800 uppercase tracking-wider">Monthly</th>
                        <th className="px-3 py-2 text-right text-xs font-bold text-green-800 uppercase tracking-wider">Bonus</th>
                        <th className="px-3 py-2 text-right text-xs font-bold text-green-800 uppercase tracking-wider">Extra Pay</th>
                        <th className="px-3 py-2 text-right text-xs font-bold text-green-800 uppercase tracking-wider">Net</th>
                        <th className="px-3 py-2 text-left text-xs font-bold text-green-800 uppercase tracking-wider">Calc Type</th>
                        <th className="px-3 py-2 text-left text-xs font-bold text-green-800 uppercase tracking-wider">Status</th>
                        <th className="px-3 py-2 text-left text-xs font-bold text-green-800 uppercase tracking-wider">Period</th>
                        <th className="px-3 py-2 text-center text-xs font-bold text-green-800 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-green-100 bg-white">
                      {salaryRecords.map((record) => {
                        const isMenuOpen = openMenuId === record.salary_id;

                        return (
                          <tr key={record.salary_id} className="hover:bg-green-50 transition-all duration-200">
                            <td className="px-3 py-2">
                              <div
                                className="flex items-center space-x-2 cursor-pointer group"
                                onClick={() => handleViewMore(record)}
                              >
                                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                                  <span className="text-sm font-bold text-white">
                                    {record.name?.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                <div>
                                  <div className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors text-sm">
                                    {record.name}
                                  </div>
                                  <div className="text-xs text-gray-500">{record.employeeID}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">
                              {formatDateForDisplay(record.SalaryDate)}
                            </td>
                            <td className="px-3 py-2">
                              <span className="inline-flex px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                                {record.mode}
                              </span>
                            </td>
                            <td className="px-3 py-2">
                              <span className="inline-flex px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">
                                {formatDepartment(
                                  departmentList.find(
                                    (e: any) => e.id === record.department
                                  )?.name
                                )}
                              </span>
                            </td>
                            <td className="px-3 py-2">
                              <span className="font-mono text-xs font-semibold text-emerald-600">
                                {formatSalaryId(record.employeeID)}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              <div className="font-bold text-green-600 text-sm">
                                ₹{record.monthlySalary?.toLocaleString()}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              <div className="font-semibold text-amber-600 text-sm">
                                ₹{(record.bonus || 0)?.toLocaleString()}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              <div className="font-semibold text-blue-600 text-sm">
                                ₹{(record.extrapay || 0)?.toLocaleString()}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              <div className="font-bold text-emerald-600 text-base">
                                ₹{record.netSalary?.toLocaleString()}
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              <span className={`inline-flex px-2 py-0.5 text-xs font-bold rounded-full ${record.calculationType === "HOURLY"
                                  ? "bg-blue-500 text-white"
                                  : "bg-purple-500 text-white"
                                }`}>
                                {record.calculationType}
                              </span>
                            </td>
                            <td className="px-3 py-2">
                              <span className={`inline-flex px-2 py-0.5 text-xs font-bold rounded-full ${record.status === "SYSTEM_CALCULATED"
                                  ? "bg-green-500 text-white"
                                  : "bg-amber-500 text-white"
                                }`}>
                                {record.status === "SYSTEM_CALCULATED" ? "Calculated" : record.status}
                              </span>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div className="text-xs font-semibold text-gray-700">
                                {months.find(
                                  (m) =>
                                    m.value ===
                                    record.month?.toString().padStart(2, "0")
                                )?.label}{" "}
                                {record.year}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-center">
                              <div className="relative" ref={menuRef}>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenMenuId(isMenuOpen ? null : record.salary_id);
                                  }}
                                  className="p-1 rounded hover:bg-gray-100 transition-all"
                                >
                                  <MoreVertical className="w-4 h-4 text-gray-500" />
                                </button>

                                {isMenuOpen && (
                                  <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 z-[9999] overflow-hidden">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewMore(record);
                                      }}
                                      className="w-full px-3 py-2 text-left hover:bg-green-50 flex items-center gap-2 text-gray-700 hover:text-green-600 transition-colors text-xs"
                                    >
                                      <Eye className="w-3.5 h-3.5" />
                                      View Details
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleAdjustSalary(record);
                                      }}
                                      className="w-full px-3 py-2 text-left hover:bg-blue-50 flex items-center gap-2 text-gray-700 hover:text-blue-600 transition-colors text-xs border-t border-gray-100"
                                    >
                                      <Edit2 className="w-3.5 h-3.5" />
                                      Adjust Salary
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(record);
                                      }}
                                      className="w-full px-3 py-2 text-left hover:bg-red-50 flex items-center gap-2 text-gray-700 hover:text-red-600 transition-colors text-xs border-t border-gray-100"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                      Delete
                                    </button>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden space-y-3 p-3">
                  {salaryRecords.map((record) => (
                    <div key={record.salary_id} className="bg-white rounded-lg border border-green-200 p-4 shadow-sm">
                      {/* Header with employee info */}
                      <div className="flex items-center justify-between mb-3 pb-2 border-b border-green-100">
                        <div
                          className="flex items-center space-x-2 cursor-pointer flex-1"
                          onClick={() => handleViewMore(record)}
                        >
                          <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                            <span className="text-base font-bold text-white">
                              {record.name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-gray-900 text-sm truncate">
                              {record.name}
                            </div>
                            <div className="text-xs text-gray-500">{record.employeeID}</div>
                          </div>
                        </div>
                        <div className="relative" ref={menuRef}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenMenuId(openMenuId === record.salary_id ? null : record.salary_id);
                            }}
                            className="p-1 rounded hover:bg-gray-100 transition-all"
                          >
                            <MoreVertical className="w-4 h-4 text-gray-500" />
                          </button>
                          {openMenuId === record.salary_id && (
                            <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 z-[9999] overflow-hidden">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewMore(record);
                                }}
                                className="w-full px-3 py-2 text-left hover:bg-green-50 flex items-center gap-2 text-gray-700 hover:text-green-600 transition-colors text-xs"
                              >
                                <Eye className="w-3.5 h-3.5" />
                                View Details
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAdjustSalary(record);
                                }}
                                className="w-full px-3 py-2 text-left hover:bg-blue-50 flex items-center gap-2 text-gray-700 hover:text-blue-600 transition-colors text-xs border-t border-gray-100"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                                Adjust Salary
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(record);
                                }}
                                className="w-full px-3 py-2 text-left hover:bg-red-50 flex items-center gap-2 text-gray-700 hover:text-red-600 transition-colors text-xs border-t border-gray-100"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Details grid */}
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="text-gray-500">Salary Date:</div>
                          <div className="text-gray-900 font-medium">{record.SalaryDate}</div>

                          <div className="text-gray-500">Mode:</div>
                          <div><span className="inline-flex px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">{record.mode}</span></div>

                          <div className="text-gray-500">Department:</div>
                          <div><span className="inline-flex px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">{formatDepartment(departmentList.find((e: any) => e.id === record.department)?.name)}</span></div>

                          <div className="text-gray-500">Employee ID:</div>
                          <div><span className="font-mono text-xs font-semibold text-emerald-600">{formatSalaryId(record.employeeID)}</span></div>

                          <div className="text-gray-500">Monthly Salary:</div>
                          <div className="font-bold text-green-600">₹{record.monthlySalary?.toLocaleString()}</div>

                          <div className="text-gray-500">Bonus:</div>
                          <div className="font-semibold text-amber-600">₹{(record.bonus || 0)?.toLocaleString()}</div>

                          <div className="text-gray-500">Extra Pay:</div>
                          <div className="font-semibold text-blue-600">₹{(record.extrapay || 0)?.toLocaleString()}</div>

                          <div className="text-gray-500">Net Salary:</div>
                          <div className="font-bold text-emerald-600">₹{record.netSalary?.toLocaleString()}</div>

                          <div className="text-gray-500">Calc Type:</div>
                          <div><span className={`inline-flex px-2 py-0.5 text-xs font-bold rounded-full ${record.calculationType === "HOURLY" ? "bg-blue-500 text-white" : "bg-purple-500 text-white"}`}>{record.calculationType}</span></div>

                          <div className="text-gray-500">Status:</div>
                          <div><span className={`inline-flex px-2 py-0.5 text-xs font-bold rounded-full ${record.status === "SYSTEM_CALCULATED" ? "bg-green-500 text-white" : "bg-amber-500 text-white"}`}>{record.status === "SYSTEM_CALCULATED" ? "Calculated" : record.status}</span></div>

                          <div className="text-gray-500">Period:</div>
                          <div className="text-xs font-semibold text-gray-700">{months.find(m => m.value === record.month?.toString().padStart(2, "0"))?.label} {record.year}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* No Data */}
          {!isLoading && salaryRecords.length === 0 && !employeesLoading && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl mx-auto mb-4 flex items-center justify-center">
                <DollarSign className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-bold text-gray-500 mb-1">No Salary Records</h3>
              <p className="text-sm text-gray-400">Calculate salary to view detailed breakdown</p>
            </div>
          )}
        </div>
      </div>

      {/* Dialog Modal for Attendance Breakdown */}
      {showDialog && selectedRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-5 py-3">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-white">Daily Attendance Breakdown</h2>
                  <p className="text-xs text-emerald-100">
                    {selectedRecord.name} -{" "}
                    {
                      months.find(
                        (m) =>
                          m.value ===
                          selectedRecord.month?.toString().padStart(2, "0")
                      )?.label
                    }{" "}
                    {selectedRecord.year}
                  </p>
                </div>
                <button
                  onClick={() => setShowDialog(false)}
                  className="p-1 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>

            <div className="overflow-auto max-h-[calc(90vh-80px)]">
              {dailyData.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 text-sm">No attendance data available</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs min-w-[500px]">
                    <thead className="sticky top-0 bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700">Date</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700">Hours</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700">Status</th>
                        <th className="px-3 py-2 text-right font-semibold text-gray-700">Payable</th>
                        <th className="px-3 py-2 text-right font-semibold text-gray-700">Overtime</th>
                        <th className="px-3 py-2 text-right font-semibold text-gray-700">Day Salary</th>
                        <th className="px-3 py-2 text-right font-semibold text-gray-700">OT Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {dailyData.map((day, index) => (
                        <tr key={index} className="hover:bg-gray-50 transition-colors">
                          <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">
                            {new Date(day.date).toLocaleDateString("en-IN")}
                          </td>
                          <td className="px-3 py-2 text-gray-700 font-mono">{day.hours}h</td>
                          <td className="px-3 py-2">
                            <span
                              className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${day.status === "ABSENT"
                                  ? "bg-red-100 text-red-700"
                                  : day.status === "PRESENT"
                                    ? "bg-green-100 text-green-700"
                                    : "bg-yellow-100 text-yellow-700"
                                }`}
                            >
                              {day.status}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-gray-600">{day.payable}h</td>
                          <td className="px-3 py-2 text-right font-mono text-purple-600">{day.overtime}h</td>
                          <td className="px-3 py-2 text-right font-mono font-semibold text-blue-600">
                            ₹{day.day_salary?.toLocaleString()}
                          </td>
                          <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-600">
                            ₹{day.overtime_amount?.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="px-5 py-3 bg-gray-50 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-700 text-sm">Total Net Salary:</span>
                <span className="text-xl font-bold text-emerald-600">
                  ₹
                  {dailyData
                    .reduce(
                      (acc, day) => acc + (day.day_salary || 0) + (day.overtime_amount || 0),
                      0
                    )
                    .toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manual Salary Adjustment Dialog */}
      {showAdjustDialog && selectedRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 px-5 py-3">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-white">Manual Salary Adjustment</h2>
                  <p className="text-xs text-blue-100">
                    {selectedRecord.name} -{" "}
                    {months.find(m => m.value === selectedRecord.month?.toString().padStart(2, "0"))?.label} {selectedRecord.year}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowAdjustDialog(false);
                    setSelectedRecord(null);
                    setAdjustmentData({ bonus: 0, extrapay: 0, description: "" });
                  }}
                  className="p-1 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                {/* Bonus Field */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-amber-600" />
                    Bonus Amount
                  </label>
                  <input
                    type="number"
                    value={adjustmentData.bonus}
                    onChange={(e) => setAdjustmentData({ ...adjustmentData, bonus: parseInt(e.target.value) || 0 })}
                    min="0"
                    step="100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm"
                    placeholder="Enter bonus amount"
                  />
                  <p className="text-xs text-gray-500 mt-1">Additional bonus to be added to salary</p>
                </div>

                {/* Extra Pay Field */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-blue-600" />
                    Extra Pay
                  </label>
                  <input
                    type="number"
                    value={adjustmentData.extrapay}
                    onChange={(e) => setAdjustmentData({ ...adjustmentData, extrapay: parseInt(e.target.value) || 0 })}
                    min="0"
                    step="100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm"
                    placeholder="Enter extra pay amount"
                  />
                  <p className="text-xs text-gray-500 mt-1">Extra payment to be added to salary</p>
                </div>

                {/* Description Field */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-600" />
                    Description (Optional)
                  </label>
                  <textarea
                    value={adjustmentData.description}
                    onChange={(e) => setAdjustmentData({ ...adjustmentData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm resize-none"
                    placeholder="Enter reason for adjustment..."
                  />
                </div>

                {/* Current Salary Info */}
                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Current Net Salary:</span>
                    <span className="font-semibold text-gray-900">₹{selectedRecord.netSalary?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">New Net Salary:</span>
                    <span className="font-bold text-emerald-600">
                      ₹{(selectedRecord.netSalary + adjustmentData.bonus + adjustmentData.extrapay)?.toLocaleString()}
                    </span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAdjustDialog(false);
                      setSelectedRecord(null);
                      setAdjustmentData({ bonus: 0, extrapay: 0, description: "" });
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmitAdjustment}
                    disabled={isAdjusting}
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
                  >
                    {isAdjusting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Applying...</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        <span>Apply Adjustment</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollRecord;